/**
 * Estrategia para convertir tokens de Firebase en tokens API mediante flujo OAuth2 convert_token
 * Implementación RFC 6749 completa con diagnóstico avanzado siguiendo patrones SOLID
 */

import BaseTokenStrategy from './BaseTokenStrategy';

/**
 * Implementa flujo OAuth2 de TiendaNube para convertir tokens Firebase en tokens API
 * Siguiendo estrictamente el estándar RFC 6749 y las especificaciones de TiendaNube
 */
class ConvertTokenStrategy extends BaseTokenStrategy {
  /**
   * Constructor de la estrategia
   * @param {Object} options - Opciones de configuración
   * @param {string} [options.clientId] - ID de cliente OAuth2
   * @param {string} [options.clientSecret] - Secreto de cliente OAuth2
   * @param {Object} [options.logger] - Logger para diagnóstico (opcional)
   */
  constructor(options = {}) {
    super(options);
    this.clientId = options.clientId;
    this.clientSecret = options.clientSecret;
    this.logger = options.logger || console;
  }

  /**
   * Extrae y almacena el ID de tienda de una respuesta
   * @param {Object} data - Datos de respuesta
   * @private
   */
  _extractTiendaIdFromResponse(data) {
    try {
      if (data && data.tienda_id) {
        localStorage.setItem('nubelytics_tienda_id', data.tienda_id);
        localStorage.setItem('nubelytics_tienda_timestamp', new Date().toISOString());
      }
    } catch (e) {/* Error no crítico */}
  }

  /**
   * Registra un error de forma estandarizada
   * @param {string} context - Contexto del error
   * @param {Error|string} error - Error ocurrido
   * @private
   */
  _logError(context, error) {
    this.logger.error(`[ConvertTokenStrategy:${context}]`, error.message || error);
    try {
      localStorage.setItem('oauth2_last_error', JSON.stringify({
        context,
        message: error.message || String(error),
        timestamp: new Date().toISOString()
      }));
    } catch (e) {/* Error no crítico */}
  }

  /**
   * Registra una petición para diagnóstico
   * @param {Object} user - Usuario que realiza la petición
   * @param {string} method - Método HTTP
   * @param {string} url - URL de la petición
   * @param {Object} headers - Cabeceras HTTP
   * @param {Object} params - Parámetros de la petición
   * @private
   */
  _logRequest(user, method, url, headers, params) {
    try {
      const sanitizedParams = { ...params };
      if (sanitizedParams.token) sanitizedParams.token = sanitizedParams.token.substring(0, 8) + '...';
      if (sanitizedParams.client_secret) sanitizedParams.client_secret = '***********';
      localStorage.setItem('oauth2_last_request', JSON.stringify({
        user_uid: user?.uid || 'unknown',
        method,
        url,
        headers,
        params: sanitizedParams,
        timestamp: new Date().toISOString()
      }));
    } catch (e) {/* Error no crítico */}
  }

  /**
   * Implementa el intercambio de token Firebase por token API según TiendaNube
   * @param {Object} user - Usuario autenticado de Firebase
   * @param {string} tokenToSend - Token de Firebase para intercambiar
   * @param {string} tiendaId - ID de tienda (opcional)
   * @return {Promise<Object|null>} Token normalizado {token, format, expiresIn, refreshToken} o null
   */
  async execute(user, tokenToSend, tiendaId) {
    try {
      // Registrar información para diagnóstico
      try {
        localStorage.setItem('convert_token_attempt', new Date().toISOString());
        localStorage.setItem('nubelytics_full_location', window.location.href);
      } catch (e) {/* Ignorar errores en modo privado */}

      // Obtener credenciales OAuth2
      const clientId = this.clientId || 
                      process.env.REACT_APP_API_CLIENT_ID || 
                      window.__API_CLIENT_ID__ || 
                      'nubelytics-web';
      
      const clientSecret = this.clientSecret || 
                          process.env.REACT_APP_API_CLIENT_SECRET || 
                          window.__API_CLIENT_SECRET__ || 
                          'nubelytics-web-client';
      
      // Registrar fuente de credenciales para diagnóstico
      try {
        localStorage.setItem('oauth2_credential_source',
          this.clientId ? 'constructor' :
          process.env.REACT_APP_API_CLIENT_ID ? 'env_var' :
          window.__API_CLIENT_ID__ ? 'window_global' : 'default');
      } catch (e) {/* Ignorar errores en modo privado */}

      // Obtener URL base y endpoint desde AppConfig
      const appConfig = require('../config/AppConfig').default;
      
      // CORRECCIÓN CRÍTICA: Usar getAuthUrl con la ruta correcta según RFC 6749
      // Los endpoints de OAuth2 deben estar en /auth/, no en /api/
      const fullUrl = appConfig.getAuthUrl('/convert-token');
      
      // Registrar URLs para diagnóstico
      try {
        localStorage.setItem('convert_token_url', fullUrl);
        localStorage.setItem('api_base_url', appConfig.apiBaseUrl);
        localStorage.setItem('auth_base_url', appConfig.authBaseUrl);
        localStorage.setItem('convert_token_fix_applied', 'true');
      } catch (e) {/* Ignorar errores en modo privado */}

      // Eliminar el prefijo Bearer si existe ya que este endpoint espera solo el token JWT puro
      let purifiedToken = tokenToSend;
      if (tokenToSend.startsWith('Bearer ')) {
        this.logger.debug('[ConvertTokenStrategy] Quitando prefijo Bearer para endpoint OAuth2');
        purifiedToken = tokenToSend.substring(7); // 'Bearer '.length = 7
      }
      
      // Verificación adicional del formato JWT
      const tokenParts = purifiedToken.split('.');
      if (tokenParts.length !== 3) {
        this.logger.warn(`[ConvertTokenStrategy] Token con formato inválido (partes=${tokenParts.length})`);
        try {
          localStorage.setItem('convert_token_invalid_format', 'true');
          localStorage.setItem('convert_token_parts', String(tokenParts.length));
        } catch (e) {/* Error no crítico */}
      }
      
      // Preparar parámetros según RFC 6749 y especificaciones de TiendaNube
      const params = new URLSearchParams();
      params.append('grant_type', 'convert_token');
      params.append('client_id', clientId);
      params.append('client_secret', clientSecret);
      params.append('backend', 'firebase');
      params.append('token', purifiedToken); // Usar el token sin prefijo Bearer
      if (tiendaId) params.append('tienda_id', tiendaId);
      
      // Registrar diagnóstico sanitizado
      try {
        localStorage.setItem('convert_token_params', JSON.stringify({
          grant_type: 'convert_token',
          client_id: clientId,
          client_secret: '[REDACTED]',
          backend: 'firebase',
          token_length: tokenToSend ? tokenToSend.length : 0,
          tienda_id: tiendaId || 'no-provided'
        }));
      } catch (e) {/* Ignorar errores en modo privado */}

      // Cabeceras estándar OAuth2 con soporte para TiendaNube
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json'
      };
      
      // Añadir autenticación Basic según RFC 7617 (opcional pero recomendado)
      const basicAuth = btoa(`${clientId}:${clientSecret}`);
      headers['Authorization'] = `Basic ${basicAuth}`;
      
      // Registrar request para diagnóstico (sanitizado)
      this._logRequest(
        user, 
        'POST', 
        fullUrl, 
        {...headers, Authorization: 'Basic [REDACTED]'}, 
        Object.fromEntries(params)
      );

      try {
        // Realizar petición con formato OAuth2 estándar
        const response = await fetch(fullUrl, {
          method: 'POST',
          headers,
          body: params.toString(),
          credentials: 'include' // Necesario para mantener sesión
        });
        
        // Clonar respuesta para evitar "already read" errors
        const clonedResponse = response.clone();
        
        // Si la respuesta es exitosa, procesar según OAuth2
        if (response.ok) {
          try {
            const data = await response.json();
            
            // Registrar éxito para diagnóstico
            localStorage.setItem('convert_token_success', 'true');
            localStorage.setItem('convert_token_timestamp', new Date().toISOString());
            
            // Extraer ID de tienda si existe
            this._extractTiendaIdFromResponse(data);
            
            // Normalizar formato de token según estándar
            return {
              token: data.access_token,
              format: data.token_type || 'Bearer',
              expiresIn: data.expires_in || 3600,
              refreshToken: data.refresh_token
            };
          } catch (jsonError) {
            // Error al procesar JSON de respuesta exitosa
            this._logError('json_parse', jsonError);
            localStorage.setItem('convert_token_json_error', jsonError.toString());
            return null;
          }
        }
        
        // Procesar errores HTTP
        let errorBody;
        try {
          // Intentar obtener cuerpo de error como JSON
          errorBody = await clonedResponse.json();
        } catch (e) {
          try {
            // Fallback a texto plano
            errorBody = await clonedResponse.text();
          } catch (textError) {
            errorBody = 'No se pudo leer la respuesta';
          }
        }
        
        // Registrar error HTTP para diagnóstico
        this._logError('http_error', new Error(`HTTP ${response.status}: ${JSON.stringify(errorBody)}`));
        localStorage.setItem('convert_token_error', JSON.stringify({
          status: response.status,
          statusText: response.statusText,
          body: errorBody,
          timestamp: new Date().toISOString(),
          headers: Object.fromEntries(
            Object.entries(headers).map(([k, v]) => [k, k === 'Authorization' ? '[REDACTED]' : v])
          )
        }));
        
        // Si es error 401 (Unauthorized), intentar método alternativo
        if (response.status === 401) {
          this.logger.info('[ConvertTokenStrategy] Error 401, intentando método alternativo...');
          
          // Intentar con endpoint /api/token usando token como Bearer
          return await this._tryAlternativeMethod(appConfig, clientId, clientSecret, tokenToSend, tiendaId);
        }
        
        // Otro tipo de error - fallar completamente
        return null;
        
      } catch (fetchError) {
        // Error de red/conexión
        this._logError('network', fetchError);
        localStorage.setItem('convert_token_network_error', fetchError.toString());
        return null;
      }
    } catch (error) {
      // Error general del método
      this._logError('execute', error);
      return null;
    }
  }
  
  /**
   * Método alternativo para obtener token usando directamente el endpoint /api/token
   * @param {Object} appConfig - Configuración de la aplicación
   * @param {string} clientId - ID de cliente OAuth2
   * @param {string} clientSecret - Secreto de cliente OAuth2
   * @param {string} tokenToSend - Token Firebase
   * @param {string} tiendaId - ID de tienda
   * @return {Promise<Object|null>} Token normalizado o null
   * @private
   */
  async _tryAlternativeMethod(appConfig, clientId, clientSecret, tokenToSend, tiendaId) {
    try {
      // URL alternativa - CORRECCIÓN CRÍTICA: Usar getAuthUrl con la ruta correcta según RFC 6749
      const altUrl = appConfig.getAuthUrl('/token');
      localStorage.setItem('convert_token_alt_url', altUrl);
      localStorage.setItem('convert_token_alt_fix_applied', 'true');
      
      // Formato alternativo: token firebase como Bearer + params en JSON
      const altHeaders = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${tokenToSend}`
      };
      
      const altParams = {
        client_id: clientId,
        client_secret: clientSecret
      };
      
      // Añadir tiendaId si está disponible
      if (tiendaId) {
        altParams.tienda_id = tiendaId;
      }
      
      // Hacer petición alternativa
      const altResponse = await fetch(altUrl, {
        method: 'POST',
        headers: altHeaders,
        body: JSON.stringify(altParams),
        credentials: 'include'
      });
      
      // Clonar respuesta
      const clonedAltResponse = altResponse.clone();
      
      // Si es exitoso el método alternativo
      if (altResponse.ok) {
        try {
          const altData = await altResponse.json();
          localStorage.setItem('convert_token_alt_success', 'true');
          
          // Extraer ID de tienda si existe
          this._extractTiendaIdFromResponse(altData);
          
          return {
            token: altData.access_token || altData.token,
            format: altData.token_type || 'Bearer',
            expiresIn: altData.expires_in || 3600,
            refreshToken: altData.refresh_token
          };
        } catch (altJsonError) {
          this._logError('alt_json_parse', altJsonError);
          localStorage.setItem('convert_token_alt_json_error', altJsonError.toString());
          return null;
        }
      }
      
      // Procesar error del método alternativo
      let altErrorBody;
      try {
        altErrorBody = await clonedAltResponse.json();
      } catch (e) {
        try {
          altErrorBody = await clonedAltResponse.text();
        } catch (textError) {
          altErrorBody = 'No se pudo leer la respuesta alternativa';
        }
      }
      
      // Registrar error del método alternativo
      this._logError('alt_http_error', new Error(`ALT HTTP ${altResponse.status}: ${JSON.stringify(altErrorBody)}`));
      localStorage.setItem('convert_token_alt_error', JSON.stringify({
        status: altResponse.status,
        statusText: altResponse.statusText,
        body: altErrorBody,
        timestamp: new Date().toISOString()
      }));
      
      return null;
    } catch (altError) {
      // Error general del método alternativo
      this._logError('alt_method', altError);
      localStorage.setItem('convert_token_alt_network_error', altError.toString());
      return null;
    }
  }
}

export default ConvertTokenStrategy;
